import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/utils/api";
import { useAuth } from "@/state/AuthContext";

interface Plan {
  id: string;
  label: string;
  price_cents: number;
  max_seats: number | null;
}

interface BillingStatus {
  plan_id: string;
  plan_label: string;
  price_cents: number;
  max_seats: number | null;
  subscription_status: string;
}

function formatSeats(used: number, max: number | null): string {
  if (max == null) return `${used} of unlimited`;
  return `${used} of ${max}`;
}

function subscriptionStatusLabel(status: string): string {
  switch (status) {
    case "trialing":
      return "Trial";
    case "active":
      return "Active";
    case "past_due":
      return "Past due";
    case "locked":
      return "Locked";
    case "canceled":
      return "Canceled";
    default:
      return status;
  }
}

export function BillingPage() {
  const { tenant, refreshMe } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [seatsUsed, setSeatsUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [plansData, statusData] = await Promise.all([
          apiFetch<{ plans: Plan[] }>("/billing/plans"),
          apiFetch<{ tenant: BillingStatus; seatsUsed: number }>("/billing/status"),
        ]);
        setPlans(plansData.plans);
        setBilling(statusData.tenant);
        setSeatsUsed(statusData.seatsUsed);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load billing");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const checkout = async (planId: string) => {
    try {
      const data = await apiFetch<{ url?: string; devMode?: boolean; message?: string }>(
        "/billing/checkout",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ planId }),
        },
      );
      if (data.url) window.location.href = data.url;
      else if (data.devMode) {
        await refreshMe();
        alert(data.message ?? "Plan activated");
        window.location.reload();
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Checkout failed");
    }
  };

  const openPortal = async () => {
    try {
      const data = await apiFetch<{ url: string }>("/billing/portal", { method: "POST" });
      window.location.href = data.url;
    } catch (e) {
      alert(e instanceof Error ? e.message : "Portal unavailable");
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  const currentPlanId = billing?.plan_id ?? tenant?.planId;
  const maxSeats = billing?.max_seats ?? plans.find((p) => p.id === currentPlanId)?.max_seats ?? null;
  const status = billing?.subscription_status ?? tenant?.subscriptionStatus ?? "unknown";
  const seatPercent = maxSeats != null && maxSeats > 0 ? Math.min(100, (seatsUsed / maxSeats) * 100) : null;

  return (
    <div className="space-y-6">
      <PageHeader title="Billing" subtitle={tenant?.name} />

      <div>
        <h3 className="text-lg font-semibold text-[#1e3a4f] mb-3">Plans</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => {
            const isCurrent = plan.id === currentPlanId;
            return (
              <div
                key={plan.id}
                className={`bg-white rounded-lg shadow p-4 flex flex-col ${
                  isCurrent ? "ring-2 ring-[#1e3a4f]" : ""
                }`}
              >
                {isCurrent && (
                  <span className="text-xs font-semibold text-[#1e3a4f] mb-1">Current plan</span>
                )}
                <h3 className="font-semibold text-[#1e3a4f]">{plan.label}</h3>
                <p className="text-2xl font-bold my-2">${(plan.price_cents / 100).toFixed(0)}/mo</p>
                {isCurrent ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Seats {formatSeats(seatsUsed, maxSeats)}
                    </p>
                    {seatPercent != null && (
                      <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#1e3a4f] transition-all"
                          style={{ width: `${seatPercent}%` }}
                        />
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground mt-3 mb-4">
                      Subscription: <strong>{subscriptionStatusLabel(status)}</strong>
                    </p>
                    {tenant?.trialEndsAt && (
                      <p className="text-sm text-muted-foreground">
                        Trial ends: {new Date(tenant.trialEndsAt).toLocaleDateString()}
                      </p>
                    )}
                    {tenant?.graceEndsAt && (
                      <p className="text-sm text-muted-foreground mb-4">
                        Grace ends: {new Date(tenant.graceEndsAt).toLocaleDateString()}
                      </p>
                    )}
                    <Button className="mt-auto" variant="secondary" disabled>
                      Current plan
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mb-4">
                      {plan.max_seats == null ? "Unlimited users" : `Up to ${plan.max_seats} users`}
                    </p>
                    <Button
                      className="mt-auto bg-[#1e3a4f] hover:bg-[#163041]"
                      onClick={() => checkout(plan.id)}
                    >
                      Select
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Button variant="secondary" onClick={openPortal}>
        Manage Payment Method
      </Button>
    </div>
  );
}
